import React, { useState, useEffect } from 'react';
import { Settings, Globe, Palette, Save, RefreshCw } from 'lucide-react';

const SettingsTab = () => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('financialModuleLanguage') || 'en';
  });
  const [timeZone, setTimeZone] = useState(() => {
    return localStorage.getItem('financialModuleTimeZone') || 'UTC+3';
  });
  const [currency, setCurrency] = useState(() => {
    return localStorage.getItem('financialModuleCurrency') || 'SAR';
  });
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('financialModuleTheme') || 'light';
  });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (saved) {
      const t = setTimeout(() => setSaved(false), 2000);
      return () => clearTimeout(t);
    }
  }, [saved]);

  const handleSave = () => {
    localStorage.setItem('financialModuleLanguage', language);
    localStorage.setItem('financialModuleTimeZone', timeZone);
    localStorage.setItem('financialModuleCurrency', currency);
    localStorage.setItem('financialModuleTheme', theme);
    setSaved(true);
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Financial Settings</h3>
        <button
          type="button"
          onClick={handleSave}
          className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors"
        >
          {saved ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          <span>{saved ? 'Saved' : 'Save changes'}</span>
        </button>
      </div>

      <div className="space-y-6 max-w-2xl">
        {/* Language */}
        <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <Globe className="w-5 h-5 text-gray-500" />
            <h4 className="font-semibold text-gray-900">Language</h4>
          </div>
          <p className="text-sm text-gray-600 mb-3">Choose your preferred language for the Financial & Accounting module.</p>
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
          >
            <option value="en">English</option>
            <option value="ar">العربية</option>
            <option value="fr">Français</option>
            <option value="es">Español</option>
          </select>
        </div>

        {/* Time zone */}
        <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <Settings className="w-5 h-5 text-gray-500" />
            <h4 className="font-semibold text-gray-900">Time zone</h4>
          </div>
          <p className="text-sm text-gray-600 mb-3">Set your local time zone for dates and reports.</p>
          <select
            value={timeZone}
            onChange={(e) => setTimeZone(e.target.value)}
            className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
          >
            <option value="UTC+3">Riyadh (UTC+3)</option>
            <option value="UTC+0">London (UTC+0)</option>
            <option value="UTC-5">New York (UTC-5)</option>
            <option value="UTC+8">Singapore (UTC+8)</option>
          </select>
        </div>

        {/* Currency */}
        <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <Settings className="w-5 h-5 text-gray-500" />
            <h4 className="font-semibold text-gray-900">Display currency</h4>
          </div>
          <p className="text-sm text-gray-600 mb-3">Default currency for amounts in this module.</p>
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
          >
            <option value="SAR">Saudi Riyal (SAR)</option>
            <option value="USD">US Dollar (USD)</option>
            <option value="EUR">Euro (EUR)</option>
            <option value="GBP">British Pound (GBP)</option>
          </select>
        </div>

        {/* Theme (optional) */}
        <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <Palette className="w-5 h-5 text-gray-500" />
            <h4 className="font-semibold text-gray-900">Appearance</h4>
          </div>
          <p className="text-sm text-gray-600 mb-3">Preferred theme for this module.</p>
          <select
            value={theme}
            onChange={(e) => setTheme(e.target.value)}
            className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
          >
            <option value="light">Light</option>
            <option value="dark">Dark</option>
            <option value="auto">Use system</option>
          </select>
        </div>
      </div>
    </div>
  );
};

export default SettingsTab;
